import { ReservationResponse } from '@/repositories/ReservationRepository'
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react'
import React from 'react'

interface ReservationDetailsProps {
  reservation: ReservationResponse
  isOpen: boolean
  onClose: () => void
}

export function ReservationDetails({
  reservation,
  isOpen,
  onClose,
}: ReservationDetailsProps) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  // Função para converter status de reserva para português
  const getReservationStatusLabel = (status: string) => {
    switch (status) {
      case 'waiting_checkin':
        return 'Aguardando check-in'
      case 'guest_in_house':
        return 'Hóspede na casa'
      case 'checkout_done':
        return 'Check-out feito'
      default:
        return status
    }
  }

  // Função para definir cores dos badges de status de reserva
  const getReservationStatusColor = (status: string) => {
    switch (status) {
      case 'waiting_checkin':
        return 'yellow'
      case 'guest_in_house':
        return 'green'
      case 'checkout_done':
        return 'blue'
      default:
        return 'gray'
    }
  }

  const paymentStatusColors = {
    pending: 'yellow',
    paid: 'green',
    refunded: 'purple',
  }

  const paymentStatusLabels = {
    pending: 'Pendente',
    paid: 'Pago',
    refunded: 'Reembolsado',
  }

  const InfoItem = ({
    label,
    value,
  }: {
    label: string
    value: React.ReactNode
  }) => (
    <HStack align="flex-start" spacing={2}>
      <Text fontWeight="bold" minW="140px">
        {label}:
      </Text>
      <Text flex={1}>{value || '-'}</Text>
    </HStack>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Detalhes da Reserva</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="2xl" fontWeight="bold" mb={2}>
                {reservation.properties?.title}
              </Text>
              <HStack spacing={2}>
                <Badge
                  colorScheme={getReservationStatusColor(reservation.status)}
                  px={3}
                  py={1}
                  borderRadius="lg"
                  textTransform="none"
                >
                  {getReservationStatusLabel(reservation.status)}
                </Badge>
                <Badge
                  colorScheme={
                    paymentStatusColors[
                      reservation.payment_status as keyof typeof paymentStatusColors
                    ]
                  }
                  px={3}
                  py={1}
                  borderRadius="lg"
                  textTransform="none"
                >
                  {
                    paymentStatusLabels[
                      reservation.payment_status as keyof typeof paymentStatusLabels
                    ]
                  }
                </Badge>
              </HStack>
            </Box>

            <Divider />

            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={3}>
                Informações da Reserva
              </Text>
              <VStack spacing={2} align="stretch">
                <InfoItem
                  label="Check-in"
                  value={formatDate(reservation.check_in_date)}
                />
                <InfoItem
                  label="Check-out"
                  value={formatDate(reservation.check_out_date)}
                />
                <InfoItem
                  label="Valor Total"
                  value={formatCurrency(reservation.reserve_total || 0)}
                />
                <InfoItem
                  label="Data de Criação"
                  value={
                    reservation.created_at
                      ? formatDate(reservation.created_at)
                      : '-'
                  }
                />
              </VStack>
            </Box>

            <Divider />

            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={3}>
                Informações do Cliente
              </Text>
              <VStack spacing={2} align="stretch">
                <InfoItem label="Nome" value={reservation?.client?.name} />
                <InfoItem label="Email" value={reservation?.client?.email} />
                <InfoItem label="Telefone" value={reservation?.client?.phone} />
              </VStack>
            </Box>

            {(reservation.properties?.checkin_instructions ||
              reservation.properties?.checkout_instructions) && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={3}>
                    Instruções
                  </Text>
                  <VStack spacing={2} align="stretch">
                    {reservation.properties.checkin_instructions && (
                      <Box>
                        <Text fontWeight="semibold">Check-in:</Text>
                        <Text>
                          {reservation.properties.checkin_instructions}
                        </Text>
                      </Box>
                    )}
                    {reservation.properties.checkout_instructions && (
                      <Box>
                        <Text fontWeight="semibold">Check-out:</Text>
                        <Text>
                          {reservation.properties.checkout_instructions}
                        </Text>
                      </Box>
                    )}
                  </VStack>
                </Box>
              </>
            )}

            <HStack justify="flex-end" pt={4}>
              <Button onClick={onClose}>Fechar</Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
